import { track } from "@/utils/events/tracker";
import axios from "axios";
import { toast } from "sonner";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useDispatch } from "react-redux";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { JsxElement } from "@/utils/jsxElementTypes";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { ReactFramework } from "@/utils/templateProjects";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCallback, useEffect, useRef } from "react";
import { sanitizeComponentName } from "@/components/AppHeader/utils";
import { useSession } from "@clerk/clerk-react";
import { Storyboard } from "@/utils/types";
import useActivityStore from "@/store/zustand/activityStore";
import { shouldUseAiGenerationSafe } from "@/utils/aiGenerationUtils";

interface CreateStoryboardParams {
  storyboard: Storyboard;
  /**
   * One of fileContentsToCreateWith, componentToAdd, or fileContentsSourceFilename must be passed in.
   */
  fileContentsToCreateWith?: string;

  /**
   * Component to add, which can be either an object with optional properties
   * or a JsxElement.
   */
  componentToAdd?:
    | {
        componentId?: string;
        nativeTag?: string;
        propsToSet?: any;
      }
    | JsxElement;

  /**
   * This is an additional optional parameter for creating with file contents.
   */
  fileContentsSourceFilename?: string;

  /**
   * If set, scrolls the canvas to the storyboard.
   */
  scrollToStoryboard?: boolean;

  /**
   * Action text for logging purposes.
   */
  actionText?: string;

  /**
   * Additional files to include, mapped by their paths.
   */
  extraPathToContents?: {
    [path: string]: string;
  };
}

export const MIN_STORYBOARD_WIDTH = 1;
export const MIN_STORYBOARD_HEIGHT = 1;
export const MIN_STORY_HEIGHT = 1;
export const MIN_STORY_WIDTH = 1;
export const MAX_STORYBOARD_WIDTH = 10000;
export const MAX_STORYBOARD_HEIGHT = 10000;

export const useCreateStoryboard = () => {
  const canvasId = useCanvasId();

  const project = useActiveProject();
  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const dispatch = useDispatch();

  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const { supabaseClient } = useSupabase();

  const { session } = useSession();

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  return useCallback(
    async (params: CreateStoryboardParams) => {
      if (!supabaseClient) {
        console.error("Supabase client not available");
        return null;
      }

      const {
        storyboard,
        fileContentsToCreateWith,
        componentToAdd,
        fileContentsSourceFilename,
        scrollToStoryboard,
        actionText,
        extraPathToContents,
      } = params;

      const treeElementLookup = useCodebaseStore.getState().treeElementLookup;

      track("STORYBOARD_CREATE", {
        creatingWithContents: Boolean(fileContentsToCreateWith),
        creatingWithComponent: Boolean(componentToAdd),
        type: storyboard.type,
      });

      const minWidth =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_WIDTH
          : MIN_STORYBOARD_WIDTH;
      const minHeight =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_HEIGHT
          : MIN_STORYBOARD_HEIGHT;

      if (storyboard.width < minWidth) {
        storyboard.width = minWidth;
      }

      if (storyboard.height < minHeight) {
        storyboard.height = minHeight;
      }

      if (storyboard.preCreated) {
        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          // @ts-expect-error TS18047
          canvasRef.current.storyboards.map((sb: any) =>
            sb.id === storyboard.id ? { ...storyboard, preCreated: false } : sb
          ),
          // @ts-expect-error TS2345
          canvasRef.current
        );
      } else {
        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          // @ts-expect-error TS18047
          [...canvasRef.current.storyboards, storyboard],
          // @ts-expect-error TS2345
          canvasRef.current
        );
      }

      const deleteStoryboard = async () => {
        if (storyboard.preCreated) {
          // Put it back to being preCreated
          await updateCanvasStoryboards(
            supabaseClient,
            dispatch,
            // @ts-expect-error TS18047
            canvasRef.current.storyboards.map((sb: any) =>
              sb.id === storyboard.id ? { ...sb, preCreated: true } : sb
            ),
            // @ts-expect-error TS2345
            canvasRef.current
          );
          return;
        }

        // Delete the storyboard
        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          // @ts-expect-error TS18047
          canvasRef.current.storyboards.filter(
            (sb: any) => sb.id !== storyboard.id
          ),
          // @ts-expect-error TS2345
          canvasRef.current
        );
      };

      try {
        const body = {
          componentName: sanitizeComponentName(storyboard.name || ""),
          storyboardId: storyboard.id,
          // @ts-expect-error TS18047
          canvasId: canvasRef.current.id,
        };

        const castComponentToAdd = componentToAdd as any;

        if (castComponentToAdd) {
          // This means it's a jsx element
          if (castComponentToAdd.type) {
            body["addJsxBody"] = {
              sourceElement: componentToAdd,
              useAiGeneration: shouldUseAiGenerationSafe(
                componentToAdd as JsxElement
              ),
            };
          } else {
            let sourceElement = castComponentToAdd.nativeTag
              ? {
                  componentName: castComponentToAdd.nativeTag,
                  type: "native",
                }
              : treeElementLookup[castComponentToAdd.componentId];
            body["addJsxBody"] = {
              sourceElement: sourceElement,
              propsToSet: castComponentToAdd.propsToSet,
              useAiGeneration: castComponentToAdd.componentId
                ? shouldUseAiGenerationSafe(
                    treeElementLookup[castComponentToAdd.componentId]
                  )
                : false,
            };
          }
        } else if (fileContentsToCreateWith) {
          body["fileContents"] = fileContentsToCreateWith;
          body["fileContentsSourceFilename"] = fileContentsSourceFilename;
        }

        const supabaseJWT = await getUnexpiredToken(session);

        if (extraPathToContents) {
          body["extraFiles"] = extraPathToContents;
        }

        const res = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/mutate/createUnsavedComponent`,
          body,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );

        const treeElements = res.data?.treeElementLookup;
        if (!treeElements || !Object.keys(treeElements).length) {
          toast.error(
            `Error creating storyboard, no main component found. Please make sure the code is correct for ${
              actionText || "creation"
            }`
          );
          await deleteStoryboard();
          return null;
        }

        let createdComponent: any = Object.values(treeElements).find(
          (treeElement: any) =>
            treeElement.type === "component" &&
            treeElement.componentName === storyboard.name
        );

        if (!createdComponent) {
          // Match the first component that is the default export
          createdComponent = Object.values(treeElements).find(
            (treeElement: any) =>
              treeElement.type === "component" && treeElement.defaultExport
          );
        }

        const fullFilePath: string | undefined = createdComponent
          ? createdComponent.filename
          : // @ts-ignore
            Object.values(treeElements)[0].filename;

        if (!fullFilePath) {
          toast.error("Error creating storyboard: No file path found");
          await deleteStoryboard();
          return undefined;
        }

        updateLookupsBasedOnPartialResponse(res.data);
        triggerRefetchActivityStream();

        const newStoryboard = {
          ...storyboard,
          created: true,
          created_at: new Date().toISOString(),
          fullFilePath,
          storyboardComponentCodebaseId: createdComponent?.codebaseId || null,
          scrollToStoryboard: Boolean(scrollToStoryboard),
          preCreated: false,
        };

        // @ts-expect-error TS18047
        let newStoryboards = Array.from(canvasRef.current.storyboards).filter(
          (sb: any) => sb.id !== storyboard.id
        );
        newStoryboards.push(newStoryboard);

        // Wait for the route to be updated in the file system
        await new Promise((resolve) => setTimeout(resolve, 2000));

        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          newStoryboards,
          // @ts-expect-error TS2345
          canvasRef.current
        );

        return newStoryboard;
      } catch (e) {
        logAxiosError("Error creating storyboard", e);
        toast.error("Error creating storyboard: " + getAxiosErorrMessage(e));
        await deleteStoryboard();
      }
    },
    [supabaseClient, Boolean(session), canvasId]
  );
};
